// Checkbox grid over the 50 US states + DC, for picking a provider's
// licensed-practice states. Not a native form control, so it can't be
// wired with react-hook-form's `register()` — pages drive it as a
// controlled value/onChange pair (see ProvidersPage's `watch`/`setValue`
// use, mirroring the pattern LocationsPage already uses for `kind`).

export type UsState = { code: string; name: string };

export const US_STATES: UsState[] = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

type Props = {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  error?: string | undefined;
  required?: boolean;
};

export function StatesMultiSelect({
  label,
  value,
  onChange,
  error,
  required,
}: Props) {
  const selected = new Set(value);

  function toggle(code: string) {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange([...next]);
  }

  const legendId = `states-multiselect-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const errorId = error ? `${legendId}-err` : undefined;

  return (
    <fieldset aria-describedby={errorId}>
      <legend className="block text-sm font-medium text-slate-700">
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-red-600">
            *
          </span>
        ) : null}
      </legend>
      {/* <fieldset> is already an implicit `group` whose accessible name
          comes from the <legend> above — an extra role="group" here would
          duplicate that as a second, ambiguous match. */}
      <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-slate-300 p-3 sm:grid-cols-3 md:grid-cols-4">
        {US_STATES.map((s) => (
          <label
            key={s.code}
            className="flex items-center gap-1.5 text-sm text-slate-700"
          >
            <input
              type="checkbox"
              checked={selected.has(s.code)}
              onChange={() => toggle(s.code)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600"
            />
            {s.name}
          </label>
        ))}
      </div>
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
